import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Save, Loader2, Instagram, Youtube, Camera } from "lucide-react";
import CloudinaryUpload from "../components/CloudinaryUpload";

export default function ProfileEdit() {
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || "",
    username: user?.username || "",
    bio: user?.bio || "",
    avatar: user?.avatar || "",
    instagram_url: user?.instagram_url || "",
    youtube_url: user?.youtube_url || "",
    phone: user?.phone || "",
    country_code: user?.country_code || "+91",
  });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm(f => ({...f, [k]: e.target.value}));

  const submit = async (e) => {
    e.preventDefault();
    setMsg(""); setErr(""); setLoading(true);
    try {
      const { data } = await api.patch("/users/me", form);
      setUser(data);
      setMsg("Profile updated!");
      setTimeout(()=>nav("/profile"), 800);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="px-4 py-6">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={()=>nav(-1)} data-testid="edit-back"><ArrowLeft className="w-5 h-5"/></button>
        <h1 className="font-outfit font-extrabold text-3xl tracking-tighter">Edit Profile</h1>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="edit-form">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <img src={form.avatar || "https://images.pexels.com/photos/8119424/pexels-photo-8119424.jpeg"} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-[#00E5FF]" data-testid="edit-avatar-preview"/>
            <div className="flex-1">
              <label className="text-xs uppercase text-white/60 font-semibold flex items-center gap-1"><Camera className="w-3.5 h-3.5"/>Profile Photo</label>
              <p className="text-[10px] text-white/50 mt-0.5">Tap below to upload a new photo (max 5MB)</p>
            </div>
          </div>
          <CloudinaryUpload kind="image" maxMB={5} testId="edit-photo"
            currentUrl={null}
            onUploaded={(d) => setForm(f => ({...f, avatar: d.secure_url}))} />
          <details className="text-xs text-white/50">
            <summary className="cursor-pointer">Or paste image URL</summary>
            <input data-testid="edit-avatar" value={form.avatar} onChange={upd("avatar")} className="mt-1 w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 outline-none text-sm"/>
          </details>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold">Name</label>
          <input data-testid="edit-name" value={form.name} onChange={upd("name")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold flex items-center justify-between">
            <span>Username / ID</span>
            <span className="text-[10px] normal-case text-white/40 font-normal">a-z, 0-9, underscore · 3-30 chars</span>
          </label>
          <div className="mt-1 flex items-center rounded-2xl bg-white/5 border border-white/10 focus-within:border-[#00E5FF] overflow-hidden">
            <span className="pl-4 text-white/45 font-bold">@</span>
            <input data-testid="edit-username" value={form.username}
              onChange={(e) => setForm(f => ({ ...f, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "") }))}
              minLength={3} maxLength={30} placeholder="yourhandle"
              className="flex-1 px-2 py-3 bg-transparent outline-none"/>
          </div>
          <p className="text-[10px] text-white/45 mt-1">Changing your username also changes your public profile link.</p>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold">Bio</label>
          <textarea data-testid="edit-bio" rows={3} value={form.bio} onChange={upd("bio")} placeholder="Tell people about yourself..." className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF] resize-none"/>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold flex items-center gap-1"><Instagram className="w-3.5 h-3.5 text-[#E4405F]"/>Instagram URL</label>
          <input data-testid="edit-instagram" type="url" placeholder="https://instagram.com/yourhandle" value={form.instagram_url} onChange={upd("instagram_url")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#E4405F]"/>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold flex items-center gap-1"><Youtube className="w-3.5 h-3.5 text-[#FF0000]"/>YouTube URL</label>
          <input data-testid="edit-youtube" type="url" placeholder="https://youtube.com/@yourchannel" value={form.youtube_url} onChange={upd("youtube_url")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#FF0000]"/>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold">Phone Number</label>
          <div className="flex gap-2 mt-1">
            <select data-testid="edit-country" value={form.country_code} onChange={upd("country_code")} className="px-2 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none w-24 text-sm">
              {["+91", "+1", "+44", "+971", "+61", "+65"].map(c => <option key={c} value={c} className="bg-[#0B0F19]">{c}</option>)}
            </select>
            <input data-testid="edit-phone" type="tel" value={form.phone} onChange={(e)=>setForm(f=>({...f, phone: e.target.value.replace(/\D/g,"")}))} className="flex-1 px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none focus:border-[#00E5FF]"/>
          </div>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold">Primary Language</label>
          <select data-testid="edit-language" value={form.language} onChange={upd("language")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-white/5 border border-white/10 outline-none">
            <option value="en" className="bg-[#0B0F19]">English</option>
            <option value="hi" className="bg-[#0B0F19]">हिन्दी • Hindi</option>
            <option value="bn" className="bg-[#0B0F19]">বাংলা • Bengali</option>
            <option value="mr" className="bg-[#0B0F19]">मराठी • Marathi</option>
            <option value="te" className="bg-[#0B0F19]">తెలుగు • Telugu</option>
            <option value="ta" className="bg-[#0B0F19]">தமிழ் • Tamil</option>
            <option value="gu" className="bg-[#0B0F19]">ગુજરાતી • Gujarati</option>
            <option value="kn" className="bg-[#0B0F19]">ಕನ್ನಡ • Kannada</option>
            <option value="ml" className="bg-[#0B0F19]">മലയാളം • Malayalam</option>
            <option value="pa" className="bg-[#0B0F19]">ਪੰਜਾਬੀ • Punjabi</option>
            <option value="or" className="bg-[#0B0F19]">ଓଡ଼ିଆ • Odia</option>
          </select>
        </div>

        <div>
          <label className="text-xs uppercase text-white/60 font-semibold mb-1 block">Primary Content</label>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={()=>setForm(f=>({...f, primary_content:"reels"}))} data-testid="edit-content-reels"
              className={`p-2.5 rounded-2xl border text-xs font-bold ${form.primary_content==="reels"?"border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]":"border-white/10 bg-white/5"}`}>
              Short Video / Reels
            </button>
            <button type="button" onClick={()=>setForm(f=>({...f, primary_content:"live"}))} data-testid="edit-content-live"
              className={`p-2.5 rounded-2xl border text-xs font-bold ${form.primary_content==="live"?"border-[#FF2A5F] bg-[#FF2A5F]/10 text-[#FF2A5F]":"border-white/10 bg-white/5"}`}>
              Live Streaming
            </button>
          </div>
        </div>

        {msg && <div className="text-emerald-400 text-sm" data-testid="edit-msg">{msg}</div>}
        {err && <div className="text-[#FF2A5F] text-sm" data-testid="edit-err">{err}</div>}

        <button data-testid="edit-submit" disabled={loading} className="w-full py-3.5 rounded-full bg-[#00E5FF] text-black font-outfit font-bold neon-cyan active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4"/>Save Changes</>}
        </button>
      </form>
    </div>
  );
}
